'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FundingFormData {
  name: string;
  email: string;
  phone: string;
  loanType: string;
  loanAmount: string;
  businessName: string;
  timeframe: string;
  details: string;
}

const supportFaqs = [
  { q: 'What are your support hours?', a: 'Our support team is available Monday through Friday, 9:00 AM to 6:00 PM Eastern Time. We are closed on federal holidays.' },
  { q: 'How quickly will I receive a response?', a: 'General inquiries receive a response within 1–2 business days. Application-related questions are prioritized and typically receive a response within 4–8 business hours.' },
  { q: 'Can I check my application status online?', a: 'Yes. Log in to your dashboard at any time to view real-time application status updates, document requests, and approval notifications.' },
  { q: 'What if I have an urgent issue outside business hours?', a: 'For urgent security concerns (suspected account compromise), email security@lendershomellc.com. For all other matters, leave a detailed message and we will respond on the next business day.' },
];

export default function ContactPage() {
  const [activeTab, setActiveTab] = useState<'general' | 'funding'>('general');
  const [submitted, setSubmitted] = useState<'general' | 'funding' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const supabase = createClient();

  const [form, setForm] = useState<FormData>({ name: '', email: '', phone: '', subject: '', message: '' });
  const [fundingForm, setFundingForm] = useState<FundingFormData>({
    name: '', email: '', phone: '', loanType: '', loanAmount: '', businessName: '', timeframe: '', details: '',
  });

  const handleGeneralSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error } = await supabase.from('contact_messages').insert({
        name: form.name,
        email: form.email,
        phone: form.phone || '',
        subject: form.subject,
        message: form.message,
        inquiry_type: 'general',
      });
      if (error) throw error;
      setSubmitted('general');
    } catch (err: any) {
      console.error('Contact form error:', err);
      setSubmitError('Failed to send message. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFundingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error } = await supabase.from('contact_messages').insert({
        name: fundingForm.name,
        email: fundingForm.email,
        phone: fundingForm.phone || '',
        subject: `Funding Inquiry – ${fundingForm.loanType || 'General'}`,
        message: fundingForm.details || '',
        inquiry_type: 'funding',
        loan_type: fundingForm.loanType || '',
        loan_amount: fundingForm.loanAmount || '',
        business_name: fundingForm.businessName || '',
        timeframe: fundingForm.timeframe || '',
      });
      if (error) throw error;
      setSubmitted('funding');
    } catch (err: any) {
      console.error('Funding form error:', err);
      setSubmitError('Failed to send inquiry. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-cloud">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-10 bg-white border-b border-slate-mist/30">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-dark text-xs font-semibold font-body mb-6 tracking-wide uppercase">
            Get in Touch
          </div>
          <h1 className="font-heading text-display font-bold text-slate-dark leading-tight mb-6">
            Contact Lenders Home LLC
          </h1>
          <p className="text-lg text-slate-fog font-body leading-relaxed max-w-3xl">
            Whether you have a question about your application, need help with your account, or want to inquire about funding options, our team is here to help.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: '✉', label: 'General Support', value: 'support@lendershomellc.com', sub: 'Response within 1–2 business days' },
            { icon: '◈', label: 'Loan Inquiries', value: 'loans@lendershomellc.com', sub: 'Response within 4–8 business hours' },
            { icon: '◆', label: 'Privacy & Legal', value: 'legal@lendershomellc.com', sub: 'Response within 10 business days' },
            { icon: '◉', label: 'Business Hours', value: 'Mon–Fri, 9 AM–6 PM EST', sub: 'Closed on federal holidays' },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-2xl p-5 shadow-card border border-slate-mist/30">
              <div className="text-sky text-xl mb-2">{item.icon}</div>
              <div className="font-heading text-sm font-bold text-slate-dark mb-1">{item.label}</div>
              <div className="text-sm text-slate font-body font-medium mb-1">{item.value}</div>
              <div className="text-xs text-slate-fog font-body">{item.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Forms */}
      <section className="py-8 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Tab switcher */}
          <div className="flex gap-2 mb-8 bg-white rounded-2xl p-1.5 shadow-card border border-slate-mist/30 w-fit">
            <button
              onClick={() => { setActiveTab('general'); setSubmitted(null); setSubmitError(null); }}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold font-body transition-all duration-200 ${
                activeTab === 'general' ? 'bg-sky text-white shadow-sky/30 shadow-md' : 'text-slate-fog hover:text-slate'
              }`}
            >
              General Inquiry
            </button>
            <button
              onClick={() => { setActiveTab('funding'); setSubmitted(null); setSubmitError(null); }}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold font-body transition-all duration-200 ${
                activeTab === 'funding' ? 'bg-sky text-white shadow-sky/30 shadow-md' : 'text-slate-fog hover:text-slate'
              }`}
            >
              Funding Inquiry
            </button>
          </div>

          {/* General Contact Form */}
          {activeTab === 'general' && (
            <div className="bg-white rounded-3xl shadow-card border border-slate-mist/30 p-8">
              {submitted === 'general' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-sky-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sky text-2xl">✓</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-dark mb-2">Message Received</h3>
                  <p className="text-slate-fog font-body text-sm max-w-sm mx-auto">Thank you for reaching out. A member of our support team will respond to your inquiry within 1–2 business days.</p>
                  <button onClick={() => { setSubmitted(null); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }} className="mt-6 px-5 py-2.5 rounded-full text-sm font-semibold font-body border border-slate-mist text-slate-fog hover:border-sky/40 hover:text-slate transition-all">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-bold text-slate-dark mb-6">Send Us a Message</h2>
                  {submitError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-body">{submitError}</div>
                  )}
                  <form onSubmit={handleGeneralSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Full Name *</label>
                        <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Email Address *</label>
                        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Phone Number</label>
                        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 000-0000" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Subject *</label>
                        <select required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm">
                          <option value="">Select a subject</option>
                          <option>Application Status</option>
                          <option>Document Upload</option>
                          <option>Account Access</option>
                          <option>Loan Terms Question</option>
                          <option>Repayment Inquiry</option>
                          <option>Privacy / Data Request</option>
                          <option>Technical Issue</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Message *</label>
                      <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Please describe your inquiry in detail..." className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm resize-none" />
                    </div>
                    <button type="submit" disabled={submitting} className="w-full py-3 bg-sky text-white rounded-xl font-semibold font-body hover:bg-sky-dark transition-colors shadow-sky/30 shadow-md disabled:opacity-60 flex items-center justify-center gap-2">
                      {submitting ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending…
                        </>
                      ) : 'Send Message'}
                    </button>
                    <p className="text-xs text-slate-fog font-body text-center">By submitting this form, you agree to our <a href="/privacy" className="text-sky hover:underline">Privacy Policy</a>.</p>
                  </form>
                </>
              )}
            </div>
          )}

          {/* Funding Inquiry Form */}
          {activeTab === 'funding' && (
            <div className="bg-white rounded-3xl shadow-card border border-slate-mist/30 p-8">
              {submitted === 'funding' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-sky-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sky text-2xl">✓</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-dark mb-2">Funding Inquiry Received</h3>
                  <p className="text-slate-fog font-body text-sm max-w-sm mx-auto">Thank you for your funding inquiry. A loan specialist will review your request and contact you within 4–8 business hours.</p>
                  <button onClick={() => { setSubmitted(null); setFundingForm({ name: '', email: '', phone: '', loanType: '', loanAmount: '', businessName: '', timeframe: '', details: '' }); }} className="mt-6 px-5 py-2.5 rounded-full text-sm font-semibold font-body border border-slate-mist text-slate-fog hover:border-sky/40 hover:text-slate transition-all">
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-bold text-slate-dark mb-2">Funding Inquiry</h2>
                  <p className="text-sm text-slate-fog font-body mb-6">Tell us about your funding needs and a loan specialist will reach out to discuss your options.</p>
                  {submitError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-body">{submitError}</div>
                  )}
                  <form onSubmit={handleFundingSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Full Name *</label>
                        <input required type="text" value={fundingForm.name} onChange={(e) => setFundingForm({ ...fundingForm, name: e.target.value })} placeholder="Jane Doe" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Email Address *</label>
                        <input required type="email" value={fundingForm.email} onChange={(e) => setFundingForm({ ...fundingForm, email: e.target.value })} placeholder="jane@company.com" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Phone Number *</label>
                        <input required type="tel" value={fundingForm.phone} onChange={(e) => setFundingForm({ ...fundingForm, phone: e.target.value })} placeholder="(555) 000-0000" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Loan Type *</label>
                        <select required value={fundingForm.loanType} onChange={(e) => setFundingForm({ ...fundingForm, loanType: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm">
                          <option value="">Select loan type</option>
                          <option>Personal Loan</option>
                          <option>Business Loan</option>
                          <option>Healthcare Loan</option>
                          <option>Mortgage Assistance</option>
                          <option>Debt Consolidation</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Requested Amount *</label>
                        <select required value={fundingForm.loanAmount} onChange={(e) => setFundingForm({ ...fundingForm, loanAmount: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm">
                          <option value="">Select range</option>
                          <option>Under $5,000</option>
                          <option>$5,000 – $25,000</option>
                          <option>$25,000 – $100,000</option>
                          <option>$100,000 – $500,000</option>
                          <option>Over $500,000</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Desired Timeframe</label>
                        <select value={fundingForm.timeframe} onChange={(e) => setFundingForm({ ...fundingForm, timeframe: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm">
                          <option value="">Select timeframe</option>
                          <option>As soon as possible</option>
                          <option>Within 1 week</option>
                          <option>Within 30 days</option>
                          <option>No specific timeline</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Business Name (if applicable)</label>
                      <input type="text" value={fundingForm.businessName} onChange={(e) => setFundingForm({ ...fundingForm, businessName: e.target.value })} placeholder="Your Business Name LLC" className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate font-body uppercase tracking-wide mb-1.5">Additional Details</label>
                      <textarea rows={4} value={fundingForm.details} onChange={(e) => setFundingForm({ ...fundingForm, details: e.target.value })} placeholder="Tell us more about your funding needs..." className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-cloud font-body text-slate placeholder-slate-fog focus:outline-none focus:ring-2 focus:ring-sky/30 focus:border-sky transition-all text-sm resize-none" />
                    </div>
                    <button type="submit" disabled={submitting} className="w-full py-3 bg-sky text-white rounded-xl font-semibold font-body hover:bg-sky-dark transition-colors shadow-sky/30 shadow-md disabled:opacity-60 flex items-center justify-center gap-2">
                      {submitting ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending…
                        </>
                      ) : 'Submit Funding Inquiry'}
                    </button>
                    <p className="text-xs text-slate-fog font-body text-center">By submitting this form, you agree to our <a href="/privacy" className="text-sky hover:underline">Privacy Policy</a>.</p>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 lg:px-10 bg-white border-t border-slate-mist/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-slate-dark mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {supportFaqs.map((faq, i) => (
              <div key={i} className="border border-slate-mist/40 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-cloud transition-colors"
                >
                  <span className="font-body font-semibold text-slate text-sm">{faq.q}</span>
                  <span className={`text-slate-fog transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>▾</span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-slate-fog font-body leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
